import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Building2,
  Globe2,
  Users,
  Scale,
  Phone,
  Mail,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import ContactForm from "@/components/ContactForm";

const HERO_IMG = "/assets/home-hero.png";

const SERVICES = [
  {
    icon: Building2,
    badge: "01",
    title: "Syndic de copropriété",
    desc: "Gestion administrative, financière et technique. Espace copropriétaire, comptes séparés disponibles 24h/24 et 7j/7.",
    href: "/metiers/syndic",
    testid: "service-syndic",
  },
  {
    icon: Globe2,
    badge: "02",
    title: "Syndic en ligne",
    desc: "100% digital, espace copropriétaire 24/7, AG en visioconférence — sans renoncer à un vrai cabinet.",
    href: "/metiers/syndic-en-ligne",
    testid: "service-syndic-en-ligne",
  },
  {
    icon: Users,
    badge: "03",
    title: "Gestionnaire ASL",
    desc: "Associations Syndicales Libres : statuts, AG, budget, suivi des équipements collectifs et espaces communs.",
    href: "/metiers/gestionnaire-asl",
    testid: "service-asl",
  },
  {
    icon: Scale,
    badge: "04",
    title: "Expertise judiciaire et extra-juridictionnelle",
    desc: "Inscrit auprès de la Cour d'appel d'Aix-en-Provence. Gestion, administration, comptabilité, gouvernance et obligations légales des acteurs de la copropriété et ASL.",
    href: "/metiers/expertise",
    testid: "service-expertise",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative -mt-[96px] lg:-mt-[144px] h-[100vh] min-h-[600px] lg:min-h-[680px] overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_IMG}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/assets/video-marseille.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 gpi-hero-overlay" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-14 sm:pb-16 lg:pb-20 pt-28 sm:pt-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <span className="font-overline-lg text-[#D4B584]">Marseille — Expert près la Cour d'appel d'Aix-en-Provence</span>
            </div>
            <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-7xl font-medium leading-[1.08] sm:leading-[1.05]">
              Syndic, ASL & Expertise
            </h1>
            <p className="text-[#E2E8F0] text-base sm:text-lg lg:text-xl mt-6 sm:mt-8 max-w-2xl leading-relaxed">
              Gestion de copropriété, ASL et expertise de copropriété à Marseille et en région PACA.
            </p>
            <div className="mt-8 lg:mt-10 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <Link to="/contact" data-testid="hero-cta-contact" className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 bg-[#B8935A] text-white border border-[#B8935A] hover:bg-[#0F172A] hover:border-[#0F172A] transition-all duration-300 text-sm font-bold tracking-wide">
                Nous contacter
                <ArrowUpRight size={16} />
              </Link>
              <a
                href="tel:0687453231"
                data-testid="hero-cta-phone"
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 border-2 border-white/40 text-white hover:bg-white hover:text-[#0F172A] transition-all duration-300 text-sm font-bold tracking-wide"
              >
                <Phone size={14} /> 06 87 45 32 31
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4 SERVICES — full bento */}
      <section className="py-16 sm:py-20 bg-[#EFE8DC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-end mb-10 lg:mb-14">
            <div className="lg:col-span-5">
              <span className="font-overline-lg text-[#B8935A]">Nos 4 métiers</span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mt-4 leading-tight">
                Une expertise complète, <span className="font-bold">un seul interlocuteur.</span>
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-[#475569] text-base sm:text-lg leading-relaxed">
                Du syndic traditionnel à la mission d'expertise judiciaire, en passant par notre offre
                100% en ligne et la gestion d'ASL&nbsp;: chaque métier est conduit avec la même rigueur
                méthodologique d'expert.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
            {SERVICES.map((s) => (
              <Link
                key={s.title}
                to={s.href}
                data-testid={s.testid}
                className="bg-white border border-white p-8 sm:p-10 lg:p-12 flex flex-col justify-between min-h-[260px] sm:min-h-[280px] relative overflow-hidden group hover:bg-[#0F172A] transition-all duration-500"
              >
                <div>
                  <div className="flex items-start justify-between">
                    <s.icon size={36} strokeWidth={1.3} className="text-[#B8935A] group-hover:text-[#D4B584] transition-colors" />
                    <span className="font-overline text-[#94A3B8] group-hover:text-[#94A3B8] transition-colors">{s.badge}</span>
                  </div>
                  <h3 className="font-serif text-2xl lg:text-3xl text-[#0F172A] group-hover:text-white mt-8 leading-tight transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-[#64748B] group-hover:text-[#CBD5E1] mt-4 leading-relaxed transition-colors">
                    {s.desc}
                  </p>
                </div>
                <div className="mt-8 flex items-center gap-2 text-[#0F172A] group-hover:text-[#D4B584] text-sm font-bold tracking-wider uppercase transition-colors">
                  En savoir plus
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT QUICK */}
      <section className="py-16 sm:py-20 lg:py-24" id="contact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-20">
            <div className="lg:col-span-5">
              <span className="font-overline-lg text-[#B8935A]">Contact</span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] mt-4 leading-tight">
                Échangeons sur votre projet.
              </h2>
              <p className="text-[#64748B] mt-5 sm:mt-6 leading-relaxed text-base sm:text-lg">
                Décrivez-nous votre besoin, nous vous répondons sous 48 heures ouvrées.
                Premier rendez-vous gratuit, sans engagement.
              </p>

              <div className="mt-10 lg:mt-12 space-y-5">
                <ContactInfo icon={Phone} label="Téléphone" value="06 87 45 32 31" />
                <ContactInfo icon={Mail} label="Email" value="contact@cabinetgpi-immobilier.fr" />
                <ContactInfo icon={MapPin} label="Adresse" value="34 rue Saint-Bruno, 13004 Marseille" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <ContactForm testIdPrefix="home-contact" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactInfo({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 border border-[#B8935A] flex items-center justify-center shrink-0">
        <Icon size={16} className="text-[#B8935A]" />
      </div>
      <div>
        <div className="font-overline text-[#94A3B8] text-[10px]">{label}</div>
        <div className="text-[#0F172A] font-medium mt-1">{value}</div>
      </div>
    </div>
  );
}
